/**
 * Sector Manager — full CRUD for the `sectors` collection.
 *
 * Operators can:
 *  • Draw a new sector boundary on the canvas
 *  • Edit an existing boundary by dragging vertices
 *  • Rename, toggle active, set demand weight and surge multiplier
 *  • Merge multiple sectors into one (preserving block history)
 *  • Soft-delete (deactivate) a sector
 *
 * Persistence: every write hits /admin/v2/sectors* and is marked
 * `manual=true` on the server so the marketplace seed will not clobber
 * the operator edits on subsequent reboots.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, Combine, Power, PowerOff, RefreshCw, X, MapPin,
} from "lucide-react";

import { api, formatApiError } from "@/shared/lib/api";
import AdminShell from "@/apps/admin/v2/AdminShell";
import { Card, CardHeader, PillButton } from "@/apps/admin/v2/components/Card";
import SectorPolygonMap from "@/apps/admin/v2/components/SectorPolygonMap";

const empty = { name: "", polygon: [], demand_weight: 1.0, surge_multiplier: 1.0, active: true };

export default function SectorManager() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState([]);
  const [mergeTarget, setMergeTarget] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/v2/sectors?include_inactive=true");
      setSectors(data.sectors || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load sectors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditingId("__new__");
    setDraft({ ...empty });
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setDraft({
      name: s.name,
      polygon: s.polygon || [],
      demand_weight: Number(s.demand_weight ?? 1.0),
      surge_multiplier: Number(s.surge_multiplier ?? 1.0),
      active: !!s.active,
    });
  };

  const cancelEdit = () => { setEditingId(null); setDraft(empty); };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Name is required");
    if ((draft.polygon || []).length < 3) return toast.error("Polygon needs at least 3 points");
    setSaving(true);
    try {
      if (editingId === "__new__") {
        await api.post("/admin/v2/sectors", {
          name: draft.name.trim(),
          city_id: "kc",
          polygon: draft.polygon,
          demand_weight: Number(draft.demand_weight) || 1.0,
          surge_multiplier: Number(draft.surge_multiplier) || 1.0,
          active: !!draft.active,
        });
        toast.success("Sector created");
      } else {
        await api.patch(`/admin/v2/sectors/${editingId}`, {
          name: draft.name.trim(),
          polygon: draft.polygon,
          demand_weight: Number(draft.demand_weight) || 1.0,
          surge_multiplier: Number(draft.surge_multiplier) || 1.0,
          active: !!draft.active,
        });
        toast.success("Sector updated");
      }
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/v2/sectors/${s.id}`, { active: !s.active });
      toast.success(`Sector ${s.active ? "deactivated" : "activated"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const softDelete = async (s) => {
    if (!window.confirm(`Deactivate "${s.name}"? Existing blocks stay readable.`)) return;
    try {
      const { data } = await api.delete(`/admin/v2/sectors/${s.id}`);
      toast.success(`Soft-deleted (${data.blocks_remaining} block(s) preserved)`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const runMerge = async () => {
    if (!mergeTarget) return toast.error("Pick a target");
    const sources = mergeSelection.filter((id) => id !== mergeTarget);
    if (sources.length === 0) return toast.error("Pick at least one source");
    if (!window.confirm(`Merge ${sources.length} sector(s) into ${sectors.find((s) => s.id === mergeTarget)?.name}? Blocks will be reassigned and sources deactivated.`)) return;
    try {
      const { data } = await api.post("/admin/v2/sectors/merge", { target_id: mergeTarget, source_ids: sources });
      toast.success(`Merged · ${data.blocks_reassigned} blocks reassigned`);
      setMergeMode(false); setMergeSelection([]); setMergeTarget("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Merge failed");
    }
  };

  // Items rendered on the map: existing sectors + the draft polygon (if any) shown live.
  const mapItems = useMemo(() => {
    return sectors.map((s) => ({
      id: s.id,
      name: s.name,
      polygon: editingId === s.id ? draft.polygon : s.polygon,
      center: s.center,
      health: s.active ? "balanced" : "inactive",
      open_blocks: 0,
    }));
  }, [sectors, editingId, draft.polygon]);

  return (
    <AdminShell
      title="Sector Manager"
      subtitle="Draw, edit, merge, and tune the dispatch territories that power every block, every quote, every coverage metric."
      headerActions={
        <>
          <PillButton onClick={load} variant="secondary" testid="sectors-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </PillButton>
          {!mergeMode ? (
            <>
              <PillButton onClick={() => setMergeMode(true)} variant="secondary" testid="sectors-merge-mode">
                <Combine size={13} /> Merge sectors
              </PillButton>
              <PillButton onClick={startNew} variant="primary" testid="sectors-new">
                <Plus size={13} /> New sector
              </PillButton>
            </>
          ) : (
            <>
              <PillButton onClick={() => { setMergeMode(false); setMergeSelection([]); setMergeTarget(""); }} variant="ghost" testid="sectors-merge-cancel">
                <X size={13} /> Cancel
              </PillButton>
              <PillButton onClick={runMerge} variant="primary" testid="sectors-merge-confirm">
                <Combine size={13} /> Run merge
              </PillButton>
            </>
          )}
        </>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Map */}
        <Card className="xl:col-span-2 overflow-hidden" testid="sectors-map">
          <CardHeader
            eyebrow={editingId ? "Editing boundary" : "Sector boundaries"}
            title={editingId === "__new__" ? "New sector — click to add vertices" : editingId ? `Editing ${draft.name || "sector"}` : "All KC sectors"}
            action={editingId ? (
              <PillButton onClick={() => setDraft({ ...draft, polygon: [] })} variant="ghost" testid="sectors-clear-poly">
                <X size={13} /> Clear polygon
              </PillButton>
            ) : null}
          />
          <div className="p-3">
            <SectorPolygonMap
              items={editingId ? mapItems.filter((it) => it.id !== editingId) : mapItems}
              height={520}
              editable={!!editingId}
              drawingPolygon={editingId ? draft.polygon : null}
              onPolygonChange={editingId ? (p) => setDraft({ ...draft, polygon: p }) : null}
              bboxItems={sectors}
            />
          </div>
        </Card>

        {/* Right rail */}
        <div className="space-y-4">
          {editingId && (
            <Card testid="sectors-edit-panel">
              <CardHeader eyebrow={editingId === "__new__" ? "Create" : "Edit"} title="Sector details" />
              <div className="p-5 space-y-3">
                <Field label="Name">
                  <input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-[#0F172A]/[0.12] text-[13px]"
                    placeholder="Northland"
                    data-testid="sectors-edit-name"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Demand weight">
                    <input
                      type="number" step="0.1" min="0" max="10"
                      value={draft.demand_weight}
                      onChange={(e) => setDraft({ ...draft, demand_weight: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg border border-[#0F172A]/[0.12] text-[13px]"
                      data-testid="sectors-edit-weight"
                    />
                  </Field>
                  <Field label="Surge multiplier">
                    <input
                      type="number" step="0.05" min="0.5" max="5"
                      value={draft.surge_multiplier}
                      onChange={(e) => setDraft({ ...draft, surge_multiplier: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg border border-[#0F172A]/[0.12] text-[13px]"
                      data-testid="sectors-edit-surge"
                    />
                  </Field>
                </div>
                <label className="flex items-center gap-2 text-[12px] text-[#475569]">
                  <input
                    type="checkbox"
                    checked={!!draft.active}
                    onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                    data-testid="sectors-edit-active"
                  />
                  Active (visible to drivers and used by generator)
                </label>
                <div className="text-[11px] text-[#475569] flex items-center gap-1">
                  <MapPin size={11} /> {draft.polygon.length} vertices
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-[#0F172A]/[0.06]">
                  <PillButton onClick={save} disabled={saving} variant="primary" testid="sectors-save">
                    <Save size={13} /> {saving ? "Saving…" : "Save sector"}
                  </PillButton>
                  <PillButton onClick={cancelEdit} variant="ghost" testid="sectors-cancel">
                    Cancel
                  </PillButton>
                </div>
              </div>
            </Card>
          )}

          {mergeMode && (
            <Card testid="sectors-merge-panel">
              <CardHeader eyebrow="Merge mode" title="Pick a target, then sources" />
              <div className="p-5 text-[12px] space-y-2">
                <p className="text-[#475569]">Select the <strong>target</strong> sector that absorbs the others. Their blocks will be reassigned to the target and the source sectors will be deactivated.</p>
                <div className="mt-2 space-y-1.5 max-h-64 overflow-y-auto">
                  {sectors.filter((s) => s.active).map((s) => {
                    const isTarget = mergeTarget === s.id;
                    const isSrc = mergeSelection.includes(s.id) && s.id !== mergeTarget;
                    return (
                      <div key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#F4F4F6]">
                        <input
                          type="radio"
                          name="merge-target"
                          checked={isTarget}
                          onChange={() => setMergeTarget(s.id)}
                          data-testid={`merge-target-${s.slug}`}
                        />
                        <input
                          type="checkbox"
                          checked={isSrc || isTarget}
                          disabled={isTarget}
                          onChange={(e) => {
                            setMergeSelection((prev) => e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id));
                          }}
                          data-testid={`merge-source-${s.slug}`}
                        />
                        <span className="text-[#081B4B]">{s.name}</span>
                        {isTarget && <span className="text-[10px] text-[#F97316] uppercase">target</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}

          <Card testid="sectors-list-card">
            <CardHeader eyebrow={`${sectors.length} total`} title="All sectors" />
            <div className="px-2 py-2 max-h-[440px] overflow-y-auto" data-testid="sectors-list">
              {loading ? (
                <div className="text-center py-12 text-[#475569] text-[13px]">Loading…</div>
              ) : sectors.length === 0 ? (
                <div className="text-center py-12 text-[#475569] text-[13px]">No sectors yet.</div>
              ) : sectors.map((s) => {
                const isEditing = editingId === s.id;
                return (
                  <div
                    key={s.id}
                    className={`px-3 py-2.5 rounded-xl mb-1.5 transition-colors ${isEditing ? "bg-[#FFF7ED] ring-1 ring-[#F97316]/40" : "hover:bg-[#F4F4F6]"}`}
                    data-testid={`sectors-row-${s.slug}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#081B4B] text-[13px] truncate flex items-center gap-2">
                          {s.name}
                          {!s.active && <span className="text-[9px] tracking-wider uppercase text-[#475569] bg-slate-100 rounded px-1.5 py-0.5">inactive</span>}
                          {s.manual && <span className="text-[9px] tracking-wider uppercase text-[#F97316] bg-[#FFF7ED] rounded px-1.5 py-0.5">manual</span>}
                        </div>
                        <div className="text-[10px] text-[#475569] tracking-wider uppercase mt-0.5">
                          {s.slug} · weight {(s.demand_weight ?? 1).toFixed(1)}× · surge {(s.surge_multiplier ?? 1).toFixed(2)}×
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEdit(s)}
                          className="px-2 py-1 rounded-md text-[11px] text-[#081B4B] hover:bg-[#081B4B]/5"
                          data-testid={`sectors-edit-${s.slug}`}
                        >Edit</button>
                        <button
                          onClick={() => toggleActive(s)}
                          className="px-2 py-1 rounded-md text-[#475569] hover:text-[#081B4B]"
                          title={s.active ? "Deactivate" : "Activate"}
                          data-testid={`sectors-toggle-${s.slug}`}
                        >
                          {s.active ? <Power size={13} /> : <PowerOff size={13} />}
                        </button>
                        <button
                          onClick={() => softDelete(s)}
                          className="px-2 py-1 rounded-md text-red-600 hover:bg-red-50"
                          title="Soft-delete"
                          data-testid={`sectors-delete-${s.slug}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#475569] mb-1">{label}</div>
      {children}
    </label>
  );
}
